import React, { useState } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Eye, EyeOff } from 'lucide-react';
import PageHeader from '../../components/PageHeader';
import PasswordChecklist from '../../components/PasswordChecklist';
import * as authApi from '../../api/auth.api';
import { errorMessage } from '../../api/client';
import {
  isStrongPassword,
  PASSWORD_HELPER,
  PASSWORD_WEAK_MSG,
  PASSWORD_MISMATCH_MSG,
} from '../../utils/passwordPolicy';

function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get('token') || '';
  // Client 2026-09-10: `?portal=1` is appended when the reset was
  // requested from the Partner Portal (VENDOR / ADMIN / STAFF /
  // SUPER_ADMIN). Route the "Back to sign in" link + the post-reset
  // redirect to /portal-login instead of /login so clinics don't
  // bounce onto the patient-only Member Portal.
  const isPortal = params.get('portal') === '1';
  const signInHref = isPortal ? '/portal-login' : '/login';
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [busy, setBusy] = useState(false);

  async function submit(e) {
    e.preventDefault();
    if (!token) { toast.error('Missing reset token'); return; }
    if (!isStrongPassword(password)) { toast.error(PASSWORD_WEAK_MSG); return; }
    if (password !== confirmPassword) { toast.error(PASSWORD_MISMATCH_MSG); return; }
    setBusy(true);
    try {
      await authApi.resetPassword(token, password);
      toast.success('Password updated. Please log in.');
      navigate(signInHref);
    } catch (e) {
      toast.error(errorMessage(e));
    } finally { setBusy(false); }
  }

  return (
    <>
      <PageHeader title="Set a new password" />
      <form onSubmit={submit} className="card" style={{ maxWidth: 460 }}>
        <div className="field">
          <label>Password</label>
          <div
            className="row"
            style={{
              border: '1px solid var(--border)', borderRadius: 8,
              padding: '8px 12px', background: 'var(--surface)', gap: 8,
            }}
          >
            <input
              type={showPw ? 'text' : 'password'}
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter your password"
              style={{ flex: 1, border: 0, outline: 'none', background: 'transparent', fontSize: 16 }}
              required
            />
            <button
              type="button"
              onClick={() => setShowPw((v) => !v)}
              aria-label={showPw ? 'Hide password' : 'Show password'}
              style={{ background: 'transparent', border: 0, cursor: 'pointer', color: 'var(--ink-500)' }}
            >
              {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>
          <div className="muted text-xs" style={{ marginTop: 6 }}>{PASSWORD_HELPER}</div>
          <PasswordChecklist password={password} />
        </div>

        <div className="field">
          <label>Confirm Password</label>
          <input
            className="input"
            type={showPw ? 'text' : 'password'}
            autoComplete="new-password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            placeholder="Re-enter your password"
            required
          />
          {confirmPassword && confirmPassword !== password && (
            <div className="muted text-xs" style={{ marginTop: 6, color: 'var(--danger, #dc2626)' }}>
              {PASSWORD_MISMATCH_MSG}
            </div>
          )}
        </div>

        <button className="btn btn-primary" type="submit" disabled={busy}>Update password</button>
        <p className="muted text-sm" style={{ marginTop: 12 }}>
          <Link to={signInHref}>Back to sign in</Link>
        </p>
      </form>
    </>
  );
}

export default ResetPassword;
